// Miami University (Oxford, Ohio): public college page (#2743), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - Miami's Common Data Set 2024-2025 (cds2024-25.xlsx), the latest edition
//    Analytics and Institutional Research posts on its Common Data Set page
//    (no 2025-2026 file yet), so the counts are for Fall 2024: C1 applicants,
//    admits and enrollees with the residency table (its Unknown column is
//    blank), C8 test policy, C21 early decision counts. C22 gives early
//    action dates but no counts. Its G1/G5 costs are for 2025-2026, so they
//    are not used.
//  - Miami Admission and Aid: Admission Dates and Deadlines for Undergraduate
//    Students (fall deadlines, decision dates, the binding glossary),
//    First-Year Student Application Requirements, College Entrance Testing,
//    and Test Optional FAQs. These pages carry no cycle year; they are the
//    pages as posted for the application now open (fall 2027 entry). The
//    deadlines table's Early Decision deadline (Nov. 1) differs from the
//    2024-2025 CDS's (11/15), which describes the earlier cycle.
//  - Miami One Stop, Estimated Cost of Attendance by Cohort: the "2027
//    Cohort (entered 26-27)" Oxford undergraduate table, the latest cohort
//    posted. Miami freezes most costs for each entering cohort (its Tuition
//    Promise), so fall 2027 entrants will have their own cohort's rates,
//    not yet posted. Quirk: the Admission and Aid cost page shows a
//    different split for 2026-2027 (tuition and fees $18,890 and $42,882,
//    housing and food $17,668 as a weighted average); the cohort table's
//    itemized figures are used because its totals equal its items.
//  - College Scorecard, school id 204024 (net price by family income), read
//    from the Scorecard API on 2026-10-01.
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'Miami University Common Data Set 2024-2025',
        url: 'https://miamioh.edu/analytics-institutional-research/common-data-set.html',
        read: '2026-10-01',
    },
    deadlines: {
        label: 'Miami Admission and Aid, Admission Dates and Deadlines for Undergraduate Students',
        url: 'https://miamioh.edu/admission-aid/apply/dates-deadlines/undergraduate-students.html',
        read: '2026-10-01',
    },
    firstYear: {
        label: 'Miami Admission and Aid, First-Year Student Application Requirements',
        url: 'https://miamioh.edu/admission-aid/apply/first-year-students/index.html',
        read: '2026-10-01',
    },
    testing: {
        label: 'Miami Admission and Aid, College Entrance Testing',
        url: 'https://miamioh.edu/admission-aid/apply/first-year-students/college-entrance-testing.html',
        read: '2026-10-01',
    },
    testFaqs: {
        label: 'Miami Admission and Aid, Test Optional FAQs',
        url: 'https://miamioh.edu/admission-aid/apply/first-year-students/test-optional-faqs.html',
        read: '2026-10-01',
    },
    costs: {
        label: 'Miami One Stop, Estimated Cost of Attendance by Cohort',
        url: 'https://miamioh.edu/onestop/costs/coa-by-cohort.html',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?204024-Miami-University-Oxford',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'miami-university-oxford-ohio',
    kbId: 'miami_university_oxford_ohio',
    name: 'Miami University',
    shortName: 'Miami',
    aliases: ['Miami of Ohio', 'Miami OH'],
    location: 'Oxford, OH',
    control: 'Public',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (Miami brand guidelines); decoration only.
    branding: {
        primary: '#C41230',
        secondary: '#FFFFFF',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/miami_university_oxford_ohio_logo.png',
    },

    admission: {
        entering: 'Fall 2024',
        cdsCycle: '2024-2025',
        applied: 39580,
        admitted: 29843,
        enrolled: 4192,
        source: 'cds',
        where: 'section C1 (Miami has not posted a 2025-2026 edition)',
        residency: [
            { group: 'In-state', applied: 17842, admitted: 14348 },
            { group: 'Out-of-state', applied: 16376, admitted: 13673 },
            { group: 'International', applied: 5362, admitted: 1822 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'Miami\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate by college or major.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early decision',
        note: 'Counts for the Fall 2024 entering class. Miami\'s nonbinding early action plans are not counted in the Common Data Set, so it gives no early action rate.',
        year: 'Fall 2024',
        rows: [
            { plan: 'Early Decision', applied: 521, admitted: 438 },
        ],
        source: 'cds',
        where: 'sections C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'You apply to Miami as a first-year student. Some programs ask for more or fill early:',
        rows: [
            { school: 'College of Creative Arts', requirement: 'Some majors require a portfolio, audition or video.', source: 'firstYear' },
            { school: 'Nursing and the Farmer School of Business', requirement: 'Admission is highly competitive. December 1 is the priority deadline for nursing, majors within the Farmer School of Business and the Honors College; consideration continues after it only if space remains.', source: 'deadlines' },
        ],
        source: 'testFaqs',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Decision', value: 'February 1' },
        rows: [
            { plan: 'Early Decision', deadline: 'November 1', decision: 'December 1', binding: true },
            { plan: 'Early Action I', deadline: 'November 1', decision: 'December 15', binding: false },
            { plan: 'Early Action II', deadline: 'December 1', decision: 'February 1', binding: false },
            { plan: 'Regular Decision', deadline: 'February 1', decision: 'March 15', binding: false },
        ],
        note: 'Your application must be complete by the deadline. Admitted Early Decision students accept by January 15; everyone else by May 1. December 1 is the priority deadline for most merit scholarships and the firm deadline for the Presidential Fellows Scholarship.',
        source: 'deadlines',
        binding: 'Early Decision is binding: if admitted, you are obligated to withdraw your other applications and accept Miami\'s offer. Early Action and Regular Decision are not binding.',
        bindingSource: 'deadlines',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'Miami is test-optional for domestic first-year applicants: you do not have to send ACT or SAT scores for admission or scholarship consideration. The one exception is the Presidential Fellows Scholarship, which requires you to list your test scores on the application. You may send scores if you want them considered in Miami\'s holistic review. For the ACT, Miami reads only the composite score, and if you send several test dates it uses the composite superscore.',
        source: 'testing',
        cdsLine: 'The Common Data Set also marks the SAT or ACT as not required for admission, but considered if submitted.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'testFaqs',
    },

    cost: {
        year: '2026-2027',
        columns: ['In-state', 'Out-of-state'],
        rows: [
            { item: 'Tuition', amounts: [15616, 15616] },
            { item: 'Nonresident surcharge', amounts: [0, 24228] },
            { item: 'Fees', amounts: [3454, 3454] },
            { item: 'Housing (on campus)', amounts: [11076, 11076] },
            { item: 'Food (on campus)', amounts: [6756, 6756] },
            { item: 'Books, course materials, supplies and equipment', amounts: [1330, 1330] },
            { item: 'Transportation', amounts: [1380, 1380] },
            { item: 'Miscellaneous personal expenses', amounts: [2680, 2680] },
        ],
        totals: [42292, 66520],
        note: 'Estimates for an Oxford campus undergraduate who entered in 2026-27, the latest cohort Miami posts. Miami freezes most costs for each entering class for up to four years, so students entering in fall 2027 will have their own rates. Some majors add course or program fees.',
        source: 'costs',
        netPrice: {
            average: 29447,
            byIncome: [
                { income: '$0 to $30,000', amount: 16121 },
                { income: '$30,001 to $48,000', amount: 17881 },
                { income: '$48,001 to $75,000', amount: 21499 },
                { income: '$75,001 to $110,000', amount: 31379 },
                { income: 'Over $110,000', amount: 32613 },
            ],
            note: 'What first-year, full-time, in-state students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from Miami\'s aid offer.',
            tileDetail: 'Per year, after grants, in-state federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
